import React, { useEffect, useState } from "react";
import AppShell from "@/components/layout/AppShell";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import { Handshake, ExternalLink, ImageOff } from "lucide-react";

/**
 * Members' Sponsors page — full grid of every active partner with logo,
 * optional tier badge, description and a click-through to the sponsor's
 * website. Uses the same public endpoint the landing strip reads from,
 * so no admin bump is required for members to see updates.
 */
export default function MemberSponsors() {
  const [sponsors, setSponsors] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/sponsors/public");
        if (alive) setSponsors(data || []);
      } catch (err) {
        console.warn("[Sponsors] Failed to load sponsors:", err);
        if (alive) setSponsors([]);
      }
    })();
    return () => { alive = false; };
  }, []); // mount-only; api is a stable module import

  // Fire-and-forget click tracker so the sponsor's site opens without
  // waiting on the network round-trip. target=_blank keeps this tab
  // alive so plain fetch is enough.
  const trackClick = (id) => {
    try { api.post(`/sponsors/${id}/click`).catch((e) => console.warn("[Sponsors] click track failed:", e)); } catch (e) { console.warn("[Sponsors] trackClick error:", e); }
  };

  return (
    <AppShell scope="member">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 py-6 sm:py-8" data-testid="member-sponsors-page">
        <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.18em] text-[hsl(var(--accent))]">
              Members
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-1 flex items-center gap-2">
              <Handshake className="h-7 w-7 text-[hsl(var(--accent))]" /> Our sponsors
            </h1>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
              The businesses and partners who make Derwent Cricket Club possible. Please support the people who support us — tap a logo to visit their site.
            </p>
          </div>
        </div>

        {sponsors === null ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <Card key={i} className="h-40 rounded-2xl animate-pulse bg-[hsl(var(--secondary))]/60" />
            ))}
          </div>
        ) : sponsors.length === 0 ? (
          <Card className="p-14 text-center text-muted-foreground border-dashed rounded-2xl" data-testid="member-sponsors-empty">
            <Handshake className="h-8 w-8 mx-auto mb-3 opacity-60" />
            No sponsors listed yet — check back soon.
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {sponsors.map((s) => {
              const inner = (
                <Card
                  className="p-5 rounded-2xl h-full flex flex-col gap-3 hover:shadow-lg transition-shadow"
                  data-testid={`member-sponsor-card-${s.id}`}
                >
                  <div className="h-24 rounded-xl bg-white flex items-center justify-center overflow-hidden">
                    {s.logo_url ? (
                      <img src={s.logo_url} alt={s.name} className="h-full w-full object-contain" loading="lazy" />
                    ) : (
                      <ImageOff className="h-6 w-6 text-muted-foreground" />
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap justify-center">
                    <div className="font-semibold truncate">{s.name}</div>
                    {s.tier && (
                      <Badge variant="outline" className="text-[10px] border-[hsl(var(--accent))]/40 text-[hsl(var(--accent))]">
                        {s.tier}
                      </Badge>
                    )}
                  </div>
                  {s.description && (
                    <p className="text-sm text-muted-foreground line-clamp-4 flex-1">{s.description}</p>
                  )}
                  {s.website_url && (
                    <div className="hidden inline-flex items-center gap-1 text-xs text-[hsl(var(--accent))] font-semibold">
                      Visit site <ExternalLink className="h-3 w-3" />
                    </div>
                  )}
                </Card>
              );
              return s.website_url ? (
                <a
                  key={s.id}
                  href={s.website_url}
                  target="_blank"
                  rel="noreferrer noopener sponsored"
                  onClick={() => trackClick(s.id)}
                  aria-label={`Visit sponsor ${s.name}`}
                  data-testid={`member-sponsor-link-${s.id}`}
                >
                  {inner}
                </a>
              ) : (
                <div key={s.id}>{inner}</div>
              );
            })}
          </div>
        )}
      </div>
    </AppShell>
  );
}
